import React from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions, useTheme, Alert, Box} from '@mui/material';
import CustomButton from './Button';
import {SD_BUTTON_RADIUS} from '../../constants/ui-tokens';
import useAuthStore from '../../store/authStore';

const Modal = ({children, open, setOpen, onClose, title, buttons, modalbgColor, modalTextColor, modalTitleColor, height, titleStyles, contentStyles, maxWidth="400px", disableBackdropClose = false, disableEscapeKeyDown = false, backdropBlur = false}) => {
  const theme = useTheme();
  const bgColor = modalbgColor ? modalbgColor : theme.palette.modal.bgcolor;
  const TextColor = modalTextColor ? modalTextColor : theme.palette.modal.textcolor;
  const {isSuperAdminMasquerading, user} = useAuthStore(state => state);
  const isMasquerading = Boolean(isSuperAdminMasquerading) || Boolean(user?.is_impersonate) || Boolean(user?.isSuperAdminMasquerading) || Boolean(useAuthStore.getState().isSuperAdminMasquerading);
  const hasModifyingButton = buttons && buttons.some(button => {
    if (button.hide) return false;
    const isCancelBtn = button.type === 'outlined' || button.buttonBgColor === '#ffffff' || button.ignoreMasqueradeDisable || button.ignoreMasquerade || (button.name && (button.name.toLowerCase().includes('cancel') || button.name.toLowerCase().includes('close') || button.name.toLowerCase().includes('exit') || button.name.toLowerCase() === 'no' || button.name.toLowerCase() === 'done' || button.name.toLowerCase() === 'ok' || button.name.toLowerCase() === 'apply' || button.name.toLowerCase().includes('filter') || button.name.toLowerCase().includes('search')));
    return !isCancelBtn;
  });
  
  const handleClose = (_event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') {
      return;
    }
    if (onClose) {
      onClose();
    } else if (setOpen) {
      if (typeof setOpen === 'function') {
        setOpen(false);
      } else if (typeof setOpen === 'object' && setOpen.onClick) {
        setOpen.onClick();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      maxWidth={false}
      fullWidth={false}
      BackdropProps={
        backdropBlur
          ? {
              sx: {
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(0, 0, 0, 0.4)'
              }
            }
          : undefined
      }
      PaperProps={{
        sx: {
          backgroundColor: bgColor,
          color: TextColor,
          padding: 0,
          overflow: 'hidden',
          width: '100%',
          minWidth: { xs: '90%', sm: '370px' },
          maxWidth: maxWidth,
          borderRadius: '14px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          margin: '32px auto',
          ...(height && {height: height})
        }
      }}
    >
      <DialogTitle 
        sx={{
          backgroundColor: bgColor, 
          color: modalTitleColor, 
          padding: '16px 24px', 
          fontWeight: 800, 
          fontSize: '20px',
          ...titleStyles
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent 
        sx={{
          backgroundColor: bgColor, 
          color: TextColor, 
          padding: '24px',
          overflowY: 'auto',
          flex: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
          ...contentStyles
        }}
      >
        {isMasquerading && hasModifyingButton && (
          <Box mb={2} width="100%">
            <Alert 
              severity="warning" 
              sx={{ 
                borderRadius: '8px', 
                fontWeight: 600, 
                backgroundColor: '#fff4e5 !important', 
                border: '1px solid #ffa000',
                padding: '6px 10px',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  color: '#663c00 !important',
                  fontWeight: '600 !important',
                  fontSize: '0.78rem !important',
                  lineHeight: '1.3 !important',
                  padding: '4px 0'
                },
                '& .MuiAlert-icon': {
                  color: '#ffa000 !important',
                  marginRight: '8px',
                  padding: '4px 0'
                }
              }}
            >
              View-Only Mode: You are in Masquerade mode. Modifications are disabled.
            </Alert>
          </Box>
        )}
        {children}
      </DialogContent>

      {buttons && buttons.length > 0 && (
        <DialogActions 
          sx={{
            justifyContent: 'flex-end', 
            padding: '8px 24px', 
            gap: '12px',
            backgroundColor: bgColor
          }}
        >
          {buttons.map((button, index) => {
            if (button.hide) {
              return null;
            }
            const isCancelBtn = button.type === 'outlined' || button.buttonBgColor === '#ffffff' || button.ignoreMasqueradeDisable || button.ignoreMasquerade || (button.name && (button.name.toLowerCase().includes('cancel') || button.name.toLowerCase().includes('close') || button.name.toLowerCase().includes('exit') || button.name.toLowerCase() === 'no' || button.name.toLowerCase() === 'done' || button.name.toLowerCase() === 'ok' || button.name.toLowerCase() === 'apply' || button.name.toLowerCase().includes('filter') || button.name.toLowerCase().includes('search')));
            return (
              <CustomButton 
                type={button.type} 
                key={index} 
                onClick={button.onClick} 
                text={button?.name} 
                loading={button.loading} 
                disabled={button.disabled || (isMasquerading && !isCancelBtn)} 
                backgroundColor={button.buttonBgColor} 
                textColor={button.buttonTextColor} 
                hoverColor={button.buttonHoverColor} 
                borderRadius={button?.borderRadius ?? SD_BUTTON_RADIUS} 
                width={button.width} 
                height={button.height} 
                fontWeight={button.fontWeight} 
                textTransform={button.textTransform} 
              />
            );
          })}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
